/**
 * Versioned material-look contract for presentation VFX.
 *
 * Each surface shader module owns the colors that match its albedo.
 * Destruction FX must read these descriptors — never DestructionProfile.skin
 * and never a parallel hardcoded palette.
 */

export interface MaterialDustAppearance {
  readonly color: string;
  readonly secondaryColor?: string;
  readonly lingerScale: number;
  /** Terminal downward speed of a hung dust card (m/s). Stone fastest, sawdust slowest. */
  readonly settleSpeed: number;
}

export interface MaterialChipAppearance {
  readonly color: string;
}

/** Aged outer skin vs fresh break face — VFX fragments read these, never DestructionProfile.skin. */
export interface MaterialFragmentAppearance {
  readonly outer: string;
  readonly interior: string;
}

export interface MaterialParticleStyle {
  readonly splinters?: true;
  readonly heavyFines?: true;
  readonly plume?: true;
}

export interface MaterialAppearanceDescriptor {
  readonly version: 1;
  readonly dust: MaterialDustAppearance;
  readonly chips: MaterialChipAppearance;
  readonly debrisTint: string;
  readonly particleStyle: MaterialParticleStyle;
  readonly fragment: MaterialFragmentAppearance;
}

/** Neutral fallback (glass and unknown profiles). Cool, low-chroma motes. */
export const NEUTRAL_APPEARANCE_DESCRIPTOR: MaterialAppearanceDescriptor = {
  version: 1,
  dust: { color: '#9aa4a2', lingerScale: 0.75, settleSpeed: 0.24 },
  chips: { color: '#d2ddd9' },
  debrisTint: '#b4c2be',
  particleStyle: {},
  fragment: { outer: '#b4c2be', interior: '#d2ddd9' },
};

/** Pale sawdust — wood burst/plume must not fall back to a shared ash-grey. */
export const WOOD_SAWDUST_DUST = '#e6d9b8';
/**
 * Grey-brown mortar vs terracotta clay powder.
 * ImpactFx fits these to distinct luminances (mortar ~0.48, clay ~0.30)
 * so cards read against a lit brick wall instead of washing to white sparkle.
 */
export const BRICK_MORTAR_DUST = '#7a7268';
export const BRICK_CLAY_DUST = '#6e2c18';
/** Floor tone landing puffs mix toward so they never read as white discs. */
export const GROUND_DUST_TONE = '#5c5854';
export const LANDING_GROUND_MIX = 0.78;

function parseHexRgb(hex: string): { r: number; g: number; b: number } {
  const raw = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = Number.parseInt(
    raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw,
    16,
  );
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Mix `hex` toward `toward` by `t` in [0,1]. Returns `#rrggbb`. */
export function mixHexToward(hex: string, toward: string, t: number): string {
  const a = parseHexRgb(hex);
  const b = parseHexRgb(toward);
  const u = Math.min(1, Math.max(0, t));
  const ch = (x: number, y: number) => Math.round(x + (y - x) * u);
  const r = ch(a.r, b.r);
  const g = ch(a.g, b.g);
  const bl = ch(a.b, b.b);
  return `#${[r, g, bl].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Tint a burst/plume card actually reads. Two-tone brick and wood sawdust
 * are resolved here so the FX path cannot silently share a grey smoke hue.
 */
export function resolveBurstDustColor(
  appearance: MaterialAppearanceDescriptor,
  useSecondary: boolean,
): string {
  if (appearance.dust.secondaryColor !== undefined) {
    return useSecondary ? BRICK_CLAY_DUST : BRICK_MORTAR_DUST;
  }
  if (appearance.particleStyle.splinters) {
    return WOOD_SAWDUST_DUST;
  }
  return appearance.dust.color;
}

/** Landing puff: material dust pulled toward the ground plane. Never white. */
export function resolveLandingDustColor(appearance: MaterialAppearanceDescriptor): string {
  return mixHexToward(resolveBurstDustColor(appearance, false), GROUND_DUST_TONE, LANDING_GROUND_MIX);
}
